import { LoadingOutlined } from '@ant-design/icons';
import { Button, Flex, Form, Modal, Spin, Typography } from 'antd';
import { useEffect, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { trackButtonClick } from '../../../analytics/mixpanel';
import AngleLeft from '../../../assets/AngleLeft';
import AppAlert from '../../../CommonComponents/AppAlert/AppAlert';
import { AppForm } from '../../../CommonComponents/AppForm/AppForm';
import AppButton from '../../../CommonComponents/UI/AppButton/AppButton';
import AppInput from '../../../CommonComponents/UI/AppInput/AppInput';
import AppTextArea from '../../../CommonComponents/UI/AppTextArea/AppTextArea';
import { useActionState } from '../../../CommonFuctions/useActionState';
import { usePresets } from '../../Chatbot/usePresets';
import { useTakeTour } from '../../TakeTour/hooks/useTakeTour';
import PromptContainer from './Prompt/PromptContainer';
import styles from './WorkflowForm.module.scss';

const ACTION_STATE = {
  editConfirmModal: 'editConfirmModal',
};

const defaultExtractionField = () => ({
  id: crypto.randomUUID(),
  extractionFieldName: '',
  extractionInstruction: '',
  outputType: null,
  additionalOutputFields: [{ id: crypto.randomUUID(), fieldName: '', outputType: 'Text' }],
});

const WorkflowForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [form] = Form.useForm();
  const { actionState, setCurrentActionState, resetActionState } = useActionState();
  const {
    allPresets,
    presetsLoading,
    presetsError,
    createPreset,
    updatePreset,
    createPresetLoading,
    updatePresetLoading,
  } = usePresets();
  const isEdit = Boolean(id);
  const { currentStepInfo } = useTakeTour();

  const workflow = useMemo(() => {
    if (!isEdit) return {};
    return allPresets.find((p) => p.preset_id === id) || {};
  }, [isEdit, allPresets, id]);

  useEffect(() => {
    if (isEdit && allPresets.length > 0) {
      const presetPrompt = workflow.preset_prompt;
      const extractionFields = presetPrompt?.extractionFields?.map((item) => ({
        ...item,
        id: crypto.randomUUID(),
        additionalOutputFields: item?.additionalOutputFields?.length
          ? item.additionalOutputFields.map((i) => ({
              ...i,
              id: crypto.randomUUID(),
            }))
          : [{ id: crypto.randomUUID(), fieldName: '', outputType: 'Text' }],
      })) ?? [defaultExtractionField()];

      form.setFieldsValue({
        name: workflow.preset_name,
        description: workflow.preset_description,
        personaPrompt: presetPrompt?.personaPrompt,
        extractionFields,
      });
    }
  }, [id, form, isEdit, allPresets, workflow]);

  const goBack = () => {
    if (location.state?.fromInternal) {
      navigate(-1);
    } else {
      navigate('/settings/docchat/workflow');
    }
  };

  const buildPayload = (values) => ({
    preset_name: values.name,
    preset_description: values.description,
    preset_prompt: {
      personaPrompt: values.personaPrompt,
      extractionFields: (values.extractionFields ?? []).map(
        ({ id: extractionFieldId, ...field }) => ({
          ...field,
          additionalOutputFields: field.additionalOutputFields
            ?.filter((item) => item.fieldName)
            ?.map(({ id: additionalOutputFieldId, ...rest }) => rest),
        })
      ),
    },
  });

  const handleSubmit = async (values) => {
    if (isEdit) {
      setCurrentActionState({
        type: ACTION_STATE.editConfirmModal,
        data: values,
      });
      return;
    }
    try {
      trackButtonClick({ type: 'Workflow - Create Workflow' });
      await createPreset(buildPayload(values));
      goBack();
    } catch (error) {
      // Error handling is done in usePresets hook
    }
  };

  const handleConfirmSave = async () => {
    const values = actionState.data;
    try {
      trackButtonClick({ type: 'Workflow - Edit Workflow' });
      await updatePreset({ preset_id: id, payload: buildPayload(values) });
      resetActionState();
      goBack();
    } catch (error) {
      // Error handling is done in usePresets hook
    }
  };

  return (
    <div
      className={`${styles.container} workflow-form`}
      style={{
        width: currentStepInfo?.data?.adjustWorkflowFormWidth ? 'calc(95% - 400px)' : 'auto',
      }}
    >
      <div className={styles.header} id='workflow-form-header'>
        <Button type='text' icon={<AngleLeft width='18' height='18' />} onClick={goBack} />
        <Typography.Title level={3} className={styles.title}>
          {isEdit ? 'Edit Workflow' : 'Create a New Workflow'}
        </Typography.Title>
      </div>

      {presetsLoading && (
        <div className={styles.loaderContainer}>
          <Spin indicator={<LoadingOutlined spin />} size='large' />
        </div>
      )}

      {!presetsLoading && presetsError && (
        <Typography.Text type='danger'>{presetsError.message}</Typography.Text>
      )}

      {!presetsLoading && !presetsError && (
        <AppForm form={form} onFinish={handleSubmit} className={styles.form}>
          <AppForm.Item
            label='Workflow Name'
            name='name'
            rules={[
              { required: true, message: 'Please enter workflow name.' },
              {
                max: 50,
                message: 'Workflow name cannot exceed 50 characters.',
              },
              () => ({
                validator(_, value) {
                  if (!value) return Promise.resolve();
                  const existingWorkflow = allPresets.find(
                    (item) =>
                      item.preset_name.toLowerCase() === value.toLowerCase() &&
                      item.preset_id !== id
                  );
                  if (existingWorkflow) {
                    return Promise.reject(new Error('Workflow/Preset name already exists'));
                  }
                  return Promise.resolve();
                },
              }),
            ]}
          >
            <AppInput placeholder='Enter Name' />
          </AppForm.Item>

          <AppForm.Item
            label='Description'
            name='description'
            rules={[{ required: true, message: 'Please enter a description.' }]}
          >
            <AppTextArea placeholder='Enter description' rows={4} />
          </AppForm.Item>

          <PromptContainer
            form={form}
            setCurrentActionState={setCurrentActionState}
            defaultExtractionField={defaultExtractionField}
          />

          <div className={styles.actions}>
            <AppButton onClick={goBack}>Cancel</AppButton>
            <AppButton
              type='primary'
              loading={isEdit ? updatePresetLoading : createPresetLoading}
              htmlType='submit'
            >
              {isEdit ? 'Save changes' : 'Save'}
            </AppButton>
          </div>
        </AppForm>
      )}

      <Modal
        title={<Typography.Title level={4}>Save changes</Typography.Title>}
        open={actionState.type === ACTION_STATE.editConfirmModal}
        footer={null}
        onCancel={resetActionState}
      >
        <Typography.Paragraph className='mb-8'>
          Are you sure you want to save changes in workflow{' '}
          <b>
            &quot;
            {workflow.preset_name}
            &quot;
          </b>
          ?
        </Typography.Paragraph>
        <AppAlert
          message='Editing this workflow will delete previously generated presets.'
          type='warning'
        />
        <Flex justify='flex-end' gap={12} className='mt-24'>
          <AppButton onClick={resetActionState}>Cancel</AppButton>
          <AppButton
            variant='outlined'
            color='primary'
            loading={updatePresetLoading}
            onClick={handleConfirmSave}
          >
            Save changes
          </AppButton>
        </Flex>
      </Modal>
    </div>
  );
};

export default WorkflowForm;
